import React, { useMemo } from "react";
import Card from "../common/Card";
import Icon from "../common/Icon";
import Tooltip from "./Tooltip";
import "./LeaderRankBarChart.css";

const DEFAULT_MAX_ITEMS = 10;

const getRankColorClass = (rank) => {
    if (rank <= 3) return "leader-rank-bar-segment--top3";
    if (rank <= 5) return "leader-rank-bar-segment--top5";
    if (rank <= 10) return "leader-rank-bar-segment--top10";
    return "leader-rank-bar-segment--beyond";
};

const LeaderRankBarChart = ({
    title,
    data,
    excludedDomains = [],
    tooltipText,
    onViewAll,
    maxItems = DEFAULT_MAX_ITEMS,
}) => {
    const visibleData = useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.filter((item) => !excludedDomains.includes(item.domain));
    }, [data, excludedDomains]);

    const filteredData = useMemo(() => {
        return visibleData.slice(0, maxItems);
    }, [visibleData, maxItems]);

    const hasMoreDomains = visibleData.length > filteredData.length;
    const hasBeyondTop10 = filteredData.length > 10;

    const maxValue = useMemo(() => {
        if (filteredData.length === 0) return 0;
        const values = filteredData.map((item) => item.sortValue || 0);
        return Math.max(...values);
    }, [filteredData]);

    const renderTitle = () => {
        if (!tooltipText) {
            return <h3 className="chart-title">{title}</h3>;
        }
        return (
            <Tooltip text={tooltipText}>
                <div className="leader-rank-bar-title">
                    <h3 className="chart-title">{title}</h3>
                    <Icon name="Info" size={14} className="leader-rank-bar-info-icon" />
                </div>
            </Tooltip>
        );
    };

    const titleBlock = title ? (
        <div className="leader-rank-bar-title-block">{renderTitle()}</div>
    ) : null;

    if (filteredData.length === 0) {
        return (
            <Card className="leader-rank-bar-chart">
                {titleBlock}
                <p className="chart-empty">Aucune donnée disponible</p>
            </Card>
        );
    }

    return (
        <Card className="leader-rank-bar-chart">
            {titleBlock}

            <div className="leader-rank-bar-legend">
                <div className="legend-item">
                    <span className="legend-color legend-color--top3"></span>
                    <span className="legend-label">Top 3</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color legend-color--top5"></span>
                    <span className="legend-label">Top 5</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color legend-color--top10"></span>
                    <span className="legend-label">Top 10</span>
                </div>
                {hasBeyondTop10 && (
                    <div className="legend-item">
                        <span className="legend-color legend-color--beyond"></span>
                        <span className="legend-label">Hors top 10</span>
                    </div>
                )}
            </div>

            <div className="leader-rank-bar-list">
                {filteredData.map((item, index) => {
                    const rank = index + 1;
                    const value = item.sortValue || 0;
                    const widthPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
                    const colorClass = getRankColorClass(rank);

                    return (
                        <div key={item.domain} className="leader-rank-bar-row">
                            <span className="leader-rank-bar-rank">#{rank}</span>
                            <span className="leader-rank-bar-domain">{item.domain}</span>
                            <div className="leader-rank-bar-track">
                                <div
                                    className={`leader-rank-bar-segment ${colorClass}`}
                                    style={{ width: `${widthPercent}%` }}
                                />
                            </div>
                            <span className="leader-rank-bar-value">{item.displayValue}</span>
                        </div>
                    );
                })}
            </div>

            {onViewAll && hasMoreDomains && (
                <div className="leader-rank-bar-footer">
                    <button className="leader-rank-bar-view-all" onClick={onViewAll}>
                        Voir tous les concurrents ({visibleData.length})
                        <Icon name="ChevronRight" size={16} />
                    </button>
                </div>
            )}
        </Card>
    );
};

export default LeaderRankBarChart;
